import { FormEvent, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { setActiveArtistId } from "@/api/client";
import { useAuth } from "@/lib/authContext";
import { Button } from "@/components/ui/button";

export default function TwoFactorPage() {
  const { completeLogin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // session_id passed via router state from LoginPage
  const sessionId: string = (location.state as { session_id?: string } | null)?.session_id ?? "";

  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!sessionId) {
      setError("Session expired. Please log in again.");
      return;
    }
    setError(null);
    setLoading(true);
    try {
      const response = await fetch("/auth/verify-2fa", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId, code: code.trim() }),
      });
      if (!response.ok) {
        const data = await response.json().catch(() => null);
        throw new Error(data?.detail || "Verification failed");
      }
      const data = await response.json();
      const normalizedPermissions = Array.isArray(data.permissions) ? data.permissions : [];
      const normalizedUser =
        data.user ??
        ({
          id: data.profile?.id ?? undefined,
          email: (data.profile?.email as string | undefined) ?? "",
          name: (data.profile?.display_name as string | undefined) ?? "",
          role: data.roles?.[0]?.role ?? "owner",
          permissions: normalizedPermissions,
        } as Record<string, unknown>);
      const sessionPayload = {
        ...data,
        user: normalizedUser,
        organization: data.organization ?? null,
        teams: Array.isArray(data.teams) ? data.teams : [],
        permissions: normalizedPermissions,
        email: (normalizedUser as { email?: string }).email,
        role: (normalizedUser as { role?: string }).role,
        display_name: (normalizedUser as { name?: string }).name ?? "",
        username: (normalizedUser as { email?: string }).email ?? "",
      };
      await completeLogin(data.token, sessionPayload);
      const nextArtistId = typeof data.current === "string" ? data.current : "";
      setActiveArtistId(nextArtistId);
      navigate("/dashboard", { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Verification failed");
      setCode("");
      inputRef.current?.focus();
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="launch-root flex min-h-screen items-center justify-center px-4 text-foreground">
      <div className="launch-hero-bg" aria-hidden="true" />
      <div className="launch-orb launch-orb--one" aria-hidden="true" />
      <div className="launch-orb launch-orb--two" aria-hidden="true" />
      <form
        className="aa-glass-card relative z-10 w-full max-w-md space-y-6 rounded-2xl p-8 shadow-xl"
        onSubmit={handleSubmit}
      >
        <div className="space-y-1">
          <h1 className="aa-page-title text-2xl font-semibold text-foreground">Check your email</h1>
          <p className="text-sm text-muted-foreground">
            We sent a 6-digit verification code to your email address. It expires in 10 minutes.
          </p>
        </div>

        <div className="space-y-2">
          <label className="text-xs uppercase tracking-wide text-muted-foreground">Verification code</label>
          <input
            ref={inputRef}
            type="text"
            inputMode="numeric"
            pattern="[0-9]{6}"
            maxLength={6}
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
            className="w-full rounded-md border border-border bg-background/80 px-3 py-3 text-center text-2xl font-mono tracking-[0.5em] text-foreground/80 focus:border-primary/50 focus:outline-none"
            autoComplete="one-time-code"
            autoFocus
            required
          />
        </div>

        {error && <p className="text-xs text-rose-400">{error}</p>}

        <Button type="submit" className="w-full uppercase tracking-wide" disabled={loading || code.length !== 6}>
          {loading ? "Verifying…" : "Verify"}
        </Button>

        <p className="text-center text-xs text-muted-foreground">
          Didn't receive a code?{" "}
          <Button
            type="button"
            variant="link"
            size="sm"
            className="h-auto px-0 text-primary"
            onClick={() => navigate("/login")}
          >
            Go back and try again
          </Button>
        </p>
      </form>
    </div>
  );
}
